/**
 * Typed request helper over the Transport seam.
 *
 * Contract conventions applied here once, for every call (identity.yaml):
 *  - `Accept-Language` on every request (AR default, §11.4).
 *  - `Idempotency-Key` (UUID) on every mutation (§11.2).
 *  - `Authorization: Bearer <access>` when an access token is supplied —
 *    the token lives in memory only, never in a URL or storage (§11.1).
 *  - Non-2xx → typed ApiError from the standard envelope.
 */

import { NetworkError, toApiError } from "./errors";
import type { HttpMethod, Transport } from "./transport";

/** Current Accept-Language — kept in sync by I18nProvider. */
let acceptLanguage: "ar" | "en" = "ar";

export function setAcceptLanguage(locale: "ar" | "en"): void {
  acceptLanguage = locale;
}

export function getAcceptLanguage(): "ar" | "en" {
  return acceptLanguage;
}

function idempotencyKey(): string {
  // crypto.randomUUID is available in all supported browsers + jsdom ≥ 21.
  return crypto.randomUUID();
}

export interface RequestOptions {
  readonly method: HttpMethod;
  readonly path: string;
  readonly body?: unknown;
  readonly accessToken?: string;
  /** Contract-mandated extras, merged AFTER the conventions (never override Authorization). */
  readonly headers?: Readonly<Record<string, string>>;
}

export async function apiRequest<T>(transport: Transport, options: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {
    "Accept-Language": acceptLanguage,
    ...(options.headers ?? {}),
  };
  if (options.method !== "GET") headers["Idempotency-Key"] = idempotencyKey();
  if (options.accessToken !== undefined) headers.Authorization = `Bearer ${options.accessToken}`;

  let response;
  try {
    const request =
      options.body === undefined
        ? { method: options.method, path: options.path, headers }
        : { method: options.method, path: options.path, headers, body: options.body };
    response = await transport(request);
  } catch {
    throw new NetworkError();
  }

  if (response.status >= 200 && response.status < 300) {
    return response.body as T;
  }
  throw toApiError(response.status, response.body, response.headers);
}
